import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, MessageCircle, Monitor, Smartphone } from 'lucide-react';
import { SITE_CONFIG, WHATSAPP_MESSAGES } from '../config/siteConfig';
import { createWhatsAppUrl, trackEvent } from '../utils/analytics';
const NATUCAFE_PREVIEW = 'https://image.thum.io/get/width/700/crop/1200/noanimate/https://noizacr.com/r/natucafe-coffee-shop';
const SERVICES_PREVIEW = 'https://image.thum.io/get/width/1400/crop/900/noanimate/https://az.estrategiasdigitalesaz.com/';
const quickBenefits = ['Lista en 7 días hábiles.', 'Dominio y hosting incluidos por un año.', '₡120.000, pago único.'];
export function SingleProductHero() {
  return <section className="relative flex min-h-[82vh] items-center overflow-hidden bg-[#050511] pb-14 pt-28 md:min-h-[88vh] md:pb-16" data-id="element-1469">
      <div className="pointer-events-none absolute -right-32 top-20 h-80 w-80 rounded-full bg-[#a738f4]/10 blur-[100px]" data-id="element-1470" />
      <div className="pointer-events-none absolute bottom-10 left-0 h-64 w-64 rounded-full bg-[#20d6e8]/5 blur-[100px]" data-id="element-1471" />

      <div className="relative mx-auto grid w-full max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[1.05fr_.95fr] lg:gap-14 lg:px-8" data-id="element-1472">
        <div className="min-w-0" data-id="element-1473">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45
        }} className="mb-6 inline-flex max-w-full rounded-full border border-[#a738f4]/35 bg-[#a738f4]/10 px-4 py-2 text-center text-sm font-medium text-[#e3baff]" data-id="element-1474">
            Páginas profesionales para negocios que quieren crecer
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.08
        }} className="max-w-2xl text-4xl font-bold leading-[1.12] text-white md:text-5xl lg:text-[3.55rem]" data-id="element-1475">
            Tu negocio merece{' '}
            <span className="text-gradient-cosmic" data-id="element-1476">
              más que un perfil de Instagram.
            </span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="mt-6 max-w-xl text-lg leading-relaxed text-[#a9a9ba]" data-id="element-1477">
            Creamos una página profesional que muestra lo que vendés, genera
            confianza y lleva a tus clientes directamente a WhatsApp.
          </motion.p>

          <motion.ul initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.22
        }} className="mt-7 space-y-3" data-id="element-1478">
            {quickBenefits.map(benefit => <li key={benefit} className="flex items-center gap-3 text-sm text-[#d8d8e4]" data-id="element-1479">
                <CheckCircle2 className="h-5 w-5 flex-none text-[#20d6e8]" data-id="element-1480" />
                <span data-id="element-1481">{benefit}</span>
              </li>)}
          </motion.ul>

          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-1482">
            <a href={createWhatsAppUrl(WHATSAPP_MESSAGES.hero)} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('whatsapp_hero_click', {
            cta_location: 'hero',
            selected_product: SITE_CONFIG.product.name
          })} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#25d366] px-6 py-3 font-semibold text-[#06170c] transition-colors hover:bg-[#3ee47a] focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-1483">
              <MessageCircle className="h-5 w-5" data-id="element-1484" />
              Quiero mi página
            </a>
            <a href="#ejemplos" className="inline-flex min-h-12 items-center justify-center rounded-xl border border-white/15 px-6 py-3 font-semibold text-white transition-colors hover:border-[#20d6e8]/60 hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20d6e8]" data-id="element-1485">
              Ver ejemplos reales
            </a>
          </motion.div>
          <p className="mt-4 text-sm text-[#858598]" data-id="element-1486">
            Consulta gratuita · Sin compromiso · Atención personalizada
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        x: 24
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.65,
        delay: 0.18
      }} className="relative mx-auto w-full max-w-xl pb-10 pt-2 lg:mx-0" aria-label="Vista previa de proyectos reales en computadora y celular" data-id="element-1487">
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0c0c1c] p-2.5 shadow-2xl shadow-black/40 sm:p-3" data-id="element-1488">
            <div className="mb-3 flex items-center gap-1.5 px-1" data-id="element-1489">
              <span className="h-2 w-2 rounded-full bg-[#e92a88]" data-id="element-1490" />
              <span className="h-2 w-2 rounded-full bg-[#a738f4]" data-id="element-1491" />
              <span className="h-2 w-2 rounded-full bg-[#20d6e8]" data-id="element-1492" />
              <span className="ml-3 h-2 w-24 rounded-full bg-white/10 sm:w-32" data-id="element-1493" />
            </div>
            <div className="relative aspect-[16/10] overflow-hidden rounded-xl bg-[#101022]" data-id="element-1494">
              <img src={SERVICES_PREVIEW} alt="Vista previa de una página profesional de servicios creada por A-Z" className="h-full w-full object-cover object-top" data-id="element-1495" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-black/30" data-id="element-1496" />
              <p className="absolute bottom-3 left-3 rounded-md bg-[#050511]/85 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur" data-id="element-1497">
                Página de servicios
              </p>
            </div>
            <Monitor className="absolute -left-5 top-1/2 hidden h-10 w-10 -translate-y-1/2 rounded-xl border border-white/10 bg-[#101022] p-2 text-[#20d6e8] lg:block" data-id="element-1498" />
          </div>

          <div className="absolute bottom-0 right-3 w-[29%] min-w-[94px] max-w-[138px] rounded-[1.5rem] border-4 border-[#242438] bg-[#070713] p-1.5 shadow-2xl sm:right-5 sm:p-2" data-id="element-1499">
            <div className="mx-auto h-1.5 w-9 rounded-full bg-white/20" data-id="element-1500" />
            <div className="mt-2 aspect-[9/16] overflow-hidden rounded-[1.05rem] bg-[#101022]" data-id="element-1501">
              <img src={NATUCAFE_PREVIEW} alt="Vista previa móvil del menú digital de NatuCafé" className="h-full w-full object-cover object-top" data-id="element-1502" />
            </div>
          </div>

          <div className="absolute left-1 top-6 max-w-[132px] rounded-xl border border-[#a738f4]/30 bg-[#101022]/95 px-3 py-2 shadow-lg backdrop-blur sm:left-3 sm:max-w-none sm:px-4 sm:py-3" data-id="element-1503">
            <p className="text-[10px] text-[#a9a9ba] sm:text-xs" data-id="element-1504">
              Página lista en
            </p>
            <p className="text-xs font-bold text-white sm:text-sm" data-id="element-1505">
              {SITE_CONFIG.product.deliveryDays} días hábiles
            </p>
          </div>
          <div className="absolute bottom-12 left-1 max-w-[180px] rounded-xl border border-[#20d6e8]/30 bg-[#101022]/95 px-3 py-2 shadow-lg backdrop-blur sm:bottom-10 sm:left-auto sm:right-0 sm:max-w-none sm:px-4 sm:py-3" data-id="element-1506">
            <div className="flex items-center gap-2 text-xs font-semibold text-white sm:text-sm" data-id="element-1507">
              <Smartphone className="h-4 w-4 flex-none text-[#20d6e8]" data-id="element-1508" />
              <span data-id="element-1509">Diseño adaptable a celular</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}